export type ConsumptionMode = 'average' | 'load'

export interface ToolRecord {
  id: string
  legacyShareAliases?: string[]
  brand: string
  model: string
  toolType: string
  loadCfm: number | null
  averageCfm: number | null
  pressurePsi: number
  sourceUrl: string
  /** Human-readable scope of the manufacturer page when the URL slug alone is ambiguous. */
  sourceTitle?: string
  checkedDate: string
}

export interface CompressorRecord {
  id: string
  legacyShareAliases?: string[]
  brand: string
  model: string
  outputValue: number
  /** The matcher's internal category. "CFM" only when the source explicitly states CFM at a stated PSI. */
  outputUnit: string
  /** The manufacturer's exact printed wording for the figure, shown to the visitor as published. */
  outputLabel: string
  pressurePsi: number
  sourceUrl: string
  /** Human-readable scope of a manufacturer page when the URL slug alone is ambiguous. */
  sourceTitle?: string
  checkedDate: string
}

export interface ComparisonInput {
  tool: ToolRecord
  quantity: number
  mode: ConsumptionMode
  compressor: CompressorRecord
}

export type ComparisonResult =
  | {
      status: 'comparable'
      demand: number
      compressorOutput: number
      difference: number
      unit: 'CFM'
      pressurePsi: number
      explanation: string
    }
  | {
      status: 'not-comparable' | 'insufficient-data'
      explanation: string
    }

export function compareToolToCompressor(input: ComparisonInput): ComparisonResult {
  const { tool, compressor, quantity, mode } = input

  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 20) {
    return {
      status: 'insufficient-data',
      explanation: 'Enter a whole-number quantity from 1 to 20.',
    }
  }

  if (tool.pressurePsi !== compressor.pressurePsi) {
    return {
      status: 'not-comparable',
      explanation: `The selected ratings are published at ${tool.pressurePsi} PSI and ${compressor.pressurePsi} PSI. This MVP does not normalize across pressure conditions.`,
    }
  }

  if (compressor.outputUnit !== 'CFM') {
    return {
      status: 'not-comparable',
      explanation: `The compressor source uses “${compressor.outputUnit}”. This MVP keeps CFM and SCFM distinct instead of treating them as interchangeable.`,
    }
  }

  const perTool = mode === 'average' ? tool.averageCfm : tool.loadCfm
  if (perTool === null) {
    const availableMode = mode === 'average' && tool.loadCfm !== null
      ? { figure: 'an at-load figure', control: 'At load' }
      : mode === 'load' && tool.averageCfm !== null
        ? { figure: 'an average figure', control: 'Average' }
        : null
    return {
      status: 'insufficient-data',
      explanation: `The source record does not publish ${mode} consumption for this model.${availableMode ? ` This record publishes ${availableMode.figure}. Choose ${availableMode.control} to compare.` : ''}`,
    }
  }

  const demand = perTool * quantity
  const difference = compressor.outputValue - demand

  return {
    status: 'comparable',
    demand,
    compressorOutput: compressor.outputValue,
    difference,
    unit: 'CFM',
    pressurePsi: tool.pressurePsi,
    explanation: difference >= 0
      ? `The published compressor output is ${formatNumber(difference)} CFM above the selected tool demand at ${tool.pressurePsi} PSI.`
      : `The published compressor output is ${formatNumber(Math.abs(difference))} CFM below the selected tool demand at ${tool.pressurePsi} PSI.`,
  }
}

export function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(/\.0$/, '')
}
